import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import {
  Calendar,
  DollarSign,
  ChevronRight,
  GraduationCap,
  Trophy
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TrainingDashboardOverlay({ 
  currentTour, 
  onOpenMaintenanceDashboard,
  onOpenProgressPanel,
}) {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    try {
      const [statsRes, progressRes] = await Promise.all([
        fetch(
          `${API_URL}/api/maintenance-procedures/dashboard/stats?tour_id=${currentTour.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `${API_URL}/api/elearning/progress/tour/${currentTour.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch training stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentTour?.id]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const progressPercentage = progress?.progress_percentage || 0;
  const isComplete = progress?.completed_at != null;

  if (loading) {
    return (
      <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
        <Card className="bg-card/80 backdrop-blur-md border-border/50 h-10 flex items-center px-4 rounded-lg animate-pulse">
          <div className="flex gap-4">
            <div className="h-4 bg-muted rounded w-24"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 pointer-events-auto">
      <Card className="bg-card/90 backdrop-blur-md border-border/50 shadow-lg min-w-[400px] rounded-lg overflow-hidden">
        {/* Progress Bar at top */}
        <div className="h-1.5 bg-muted/50">
          <div 
            className={`h-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : 'bg-gradient-to-r from-purple-500 to-purple-400'}`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        <div className="flex items-center divide-x divide-border/50 h-10">
          {/* Progress - Now clickable */}
          <button
            onClick={() => onOpenProgressPanel?.()}
            className="flex items-center gap-2 px-4 h-full hover:bg-muted/50 transition-colors group"
            data-testid="progress-button"
          >
            {isComplete ? (
              <Trophy className="h-4 w-4 text-emerald-500" />
            ) : (
              <GraduationCap className="h-4 w-4 text-purple-500" />
            )}
            <span className="text-sm">Progress:</span>
            <span className={`text-sm font-bold ${isComplete ? 'text-emerald-500' : 'text-purple-500'}`}>
              {progressPercentage.toFixed(0)}%
            </span>
            <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          {/* Upcoming Maintenance */}
          <button
            onClick={() => onOpenMaintenanceDashboard?.('upcoming')}
            className="flex items-center gap-2 px-4 h-full hover:bg-muted/50 transition-colors group"
          >
            <Calendar className="h-4 w-4 text-blue-500" />
            <span className="text-sm">Upcoming:</span>
            <span className="text-sm font-bold">{stats?.summary?.upcoming_count || 0}</span>
            <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          {/* Total Costs */}
          <button
            onClick={() => onOpenMaintenanceDashboard?.('costs')}
            className="flex items-center gap-2 px-4 h-full hover:bg-muted/50 transition-colors group"
          >
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <span className="text-sm">Costs:</span>
            <span className="text-sm font-bold text-emerald-500">
              {formatCurrency(stats?.summary?.total_cost)}
            </span>
            <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </Card>
    </div>
  );
}

export default TrainingDashboardOverlay;
